import React, { useState, useEffect } from "react";
import api, { apiErr } from "../lib/api";
import { waLink, openWa, pyToday } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertTriangle, Camera, X, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const MOTIVOS = [
  { id: "sin_retorno", label: "Sin retorno del cliente" },
  { id: "no_puede_recibir", label: "El destinatario no puede recibir" },
  { id: "no_pidio", label: "El destinatario no pidió el producto" },
  { id: "otro_dia", label: "Solicitó recibir otro día" },
  { id: "direccion_incorrecta", label: "Dirección incorrecta o incompleta" },
  { id: "otro", label: "Otro motivo" },
];

const input = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

export function IncidenciaModal({ envio, onClose, onDone }) {
  const [motivo, setMotivo] = useState("sin_retorno");
  const [nota, setNota] = useState("");
  const [reagendar, setReagendar] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (envio) { setMotivo("sin_retorno"); setNota(""); setReagendar(""); setFile(null); setPreview(null); setDone(null); }
  }, [envio]);

  if (!envio) return null;
  const motivoLabel = MOTIVOS.find((m) => m.id === motivo)?.label || motivo;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (motivo === "otro_dia" && !reagendar) return toast.error("Elegí la fecha para reagendar");
    setSaving(true);
    try {
      let evidencia_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/comprobante", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evidencia_path = data.path;
      }
      const { data } = await api.post(`/envios/${envio.id}/incidencia`, {
        motivo, nota, reagendar_fecha: motivo === "otro_dia" ? reagendar : null, evidencia_path,
      });
      toast.success("Incidencia registrada");
      setDone(data);
      onDone?.();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  const avisarRemitente = () => {
    const text = `Hola ${done?.remitente_nombre || envio.remitente_nombre || ""}! Te escribimos de Pya'elino Courier. Tu envío ${envio.code} con destino a ${envio.destinatario} (${envio.ciudad}) no pudo ser entregado. Motivo: ${motivoLabel}${reagendar ? `. Se reprogramó la entrega para el ${reagendar}` : ""}.${nota ? ` Obs: ${nota}.` : ""} Ante cualquier consulta estamos a disposición.`;
    openWa(waLink(done?.remitente_whatsapp, text));
  };

  return (
    <Dialog open={!!envio} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" /> Reportar incidencia · {envio.code}</DialogTitle>
          <DialogDescription>El envío quedará marcado como incidencia y podrá resolverse desde la sucursal de destino.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3.5 text-sm text-rose-800" data-testid="incidencia-ok">
              Incidencia registrada: <b>{motivoLabel}</b>{reagendar ? ` · reprogramado para ${reagendar}` : ""}
            </div>
            {done.remitente_whatsapp ? (
              <button onClick={avisarRemitente} data-testid="incidencia-avisar-remitente"
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 text-sm">
                <MessageCircle className="h-4 w-4" /> Avisar al remitente por WhatsApp
              </button>
            ) : (
              <p className="text-xs text-center text-[#64748B]">El remitente no tiene WhatsApp registrado para avisar.</p>
            )}
            <button onClick={onClose} data-testid="incidencia-cerrar" className="w-full rounded-lg border border-slate-300 py-2.5 text-sm font-medium">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Motivo *</label>
              <select className={input} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="incidencia-motivo-select">
                {MOTIVOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            {motivo === "otro_dia" && (
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Fecha para reagendar *</label>
                <input type="date" className={input} value={reagendar} onChange={(e) => setReagendar(e.target.value)}
                  min={pyToday()} required data-testid="incidencia-fecha-input" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Observación</label>
              <textarea className={`${input} resize-none`} rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Detalle adicional (opcional)" data-testid="incidencia-nota-input" />
            </div>
            <div>
              <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-4 text-sm font-medium text-[#64748B] cursor-pointer hover:border-rose-400 hover:text-rose-500" data-testid="incidencia-evidencia-label">
                <Camera className="h-5 w-5" /> {file ? "Cambiar evidencia" : "Captura / foto de evidencia (opcional)"}
                <input type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="incidencia-evidencia-input" />
              </label>
              {preview && (
                <div className="relative mt-2">
                  <img src={preview} alt="evidencia" className="w-full h-36 object-cover rounded-lg border border-slate-200" />
                  <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"><X className="h-4 w-4 text-rose-600" /></button>
                </div>
              )}
            </div>
            <button disabled={saving} data-testid="incidencia-submit"
              className="w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Registrar incidencia
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
